import * as React from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { DataTable, type DataTableProps, type Column } from './data-table';
import { Card } from './Card';
import { Input } from './Input';
import { Spinner } from './index';

export interface ResponsiveTableProps<T> extends DataTableProps<T> {
  /**
   * Custom mobile card renderer. Receives the row and returns the card body.
   * If omitted, falls back to a stacked label/value list of all columns,
   * with any column whose key/header matches /^actions?$/ pinned to the bottom.
   */
  mobileCard?: (row: T) => React.ReactNode;
}

export function ResponsiveTable<T>(props: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile();
  if (!isMobile) {
    const { mobileCard: _ignored, ...rest } = props;
    return <DataTable {...rest} />;
  }
  return <MobileCardList {...props} />;
}

function MobileCardList<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  loading = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchKeys,
  mobileCard,
  className,
}: ResponsiveTableProps<T>) {
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!searchable || !query) return data;
    const q = query.toLowerCase();
    const keys = [...columns.map((c) => c.key as string), ...(searchKeys ?? [])];
    return data.filter((row) =>
      keys.some((key) => {
        const value = (row as any)[key];
        return value != null && String(value).toLowerCase().includes(q);
      })
    );
  }, [data, columns, searchKeys, searchable, query]);

  if (loading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {searchable && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        filtered.map((row) => (
          <Card
            key={keyExtractor(row)}
            className={cn(
              'p-4',
              onRowClick && 'cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/50'
            )}
            onClick={() => onRowClick?.(row)}
          >
            {mobileCard ? mobileCard(row) : <DefaultMobileCard row={row} columns={columns} />}
          </Card>
        ))
      )}
    </div>
  );
}

function DefaultMobileCard<T>({ row, columns }: { row: T; columns: Column<T>[] }) {
  const actionsCol = columns.find(
    (c) => /^actions?$/i.test(c.key) || /^actions?$/i.test(c.header)
  );
  const dataCols = columns.filter((c) => c !== actionsCol);
  const [first, ...rest] = dataCols;

  return (
    <>
      {first && (
        <div className="mb-2 font-medium text-foreground">
          {renderCell(row, first)}
        </div>
      )}
      {rest.length > 0 && (
        <div className="space-y-1.5 text-sm">
          {rest.map((col) => (
            <div key={col.key} className="flex items-start justify-between gap-4">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {col.header}
              </span>
              <span className="text-right">{renderCell(row, col)}</span>
            </div>
          ))}
        </div>
      )}
      {actionsCol && (
        <div className="mt-3 flex justify-end border-t border-border pt-3">
          {renderCell(row, actionsCol)}
        </div>
      )}
    </>
  );
}

function renderCell<T>(row: T, column: Column<T>): React.ReactNode {
  const value = (row as any)[column.key];
  if (column.render) return column.render(value, row);
  if (value == null) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
